/**
 * View component for /certificate-templates/:certificateTemplateId
 *
 * Displays a single certificate-template from the 'byId' map in the certificate-template reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import third-party libraries
import classNames from 'classnames';
import renderHTML from 'react-render-html';
import { DateTime } from 'luxon';

// import actions
import * as certificateTemplateActions from '../certificateTemplateActions';
import * as questionActions from '../../question/questionActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';

// import global components
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from "../../../global/components/routing/Breadcrumbs.js.jsx";
import Modal from '../../../global/components/modals/Modal.js.jsx';
import VideoPlayer from "../../../global/components/helpers/VideoPlayer.js.jsx";
import { NumberInput, SelectFromArray, SelectFromObject, TextInput } from '../../../global/components/forms';

// import module components
import CertificateTemplateLayout from '../components/CertificateTemplateLayout.js.jsx';
import QuestionEditor from '../../question/components/QuestionEditor.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';
import richContentUtils from '../../../global/utils/richContentUtils';
import videoUtils from "../../../global/utils/videoUtils.js";
import CertificateTemplatePreview from '../components/CertificateTemplatePreview.js.jsx';


class AdminSingleCertificateTemplate extends Base {
  constructor(props) {
    super(props);
    this.state = {
      editingCertificateTemplate: null
      , newQuestion: _.cloneDeep(this.props.defaultQuestion)
      , showNewQuestion: false
      , showScoringModal: false
      , status: ''
    }
    this._bind(
    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(certificateTemplateActions.fetchSingleIfNeeded(match.params.certificateTemplateId));
  }

  render() {
    const {
      certificateTemplateStore
      , location
      , match
    } = this.props;
    const { editingCertificateTemplate, newQuestion, showNewQuestion, showScoringModal } = this.state;
    /**
     * use the selected.getItem() utility to pull the actual certificate-template object from the map
     */
    const selectedCertificateTemplate = certificateTemplateStore.selected.getItem();
    // const questionList = questionStore.util.getList('_certificate-template', match.params.certificateTemplateId)
    const isEmpty = (
      !selectedCertificateTemplate
      || !selectedCertificateTemplate._id
      || certificateTemplateStore.selected.didInvalidate
    );


    return (
      <div className="flex">
        <section className="section">
          <div className="yt-container">
            <Breadcrumbs links={location.state.breadcrumbs} />
            {isEmpty ?
              (certificateTemplateStore.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
              :
              <div style={{ opacity: certificateTemplateStore.isFetching ? 0.5 : 1 }}>
                <div className="yt-row with-gutters">
                  <div className="yt-col full s_60 m_70">
                    <div className="content-container">
                      <div className="yt-row space-between">
                        <p><strong>Certificate Template </strong></p>
                        <div>
                          <Link className="yt-btn x-small info" to={`${match.url}/update`}>Update</Link>
                        </div>
                      </div>
                      <hr/>
                      <h2>
                        <small><strong>Template Name:</strong></small><br/>
                        {selectedCertificateTemplate.name}
                      </h2>
                      <br/>
                      <br/>
                      <p><small><strong>Signature Image:</strong></small></p>
                      <div className="yt-col _40">
                        <img src={selectedCertificateTemplate.signerImageUrl}/>
                      </div>
                      <p><small><strong>Signator's Full Name:</strong></small></p>
                      <p><strong>{selectedCertificateTemplate.signerName}</strong></p>
                      <p><small><strong>Signator's Position:</strong></small></p>
                      <p><strong>{selectedCertificateTemplate.signerPosition}</strong></p>
                      <br/>
                      <br/>
                      <p><small><strong>Partner Logo:</strong></small></p>
                      {selectedCertificateTemplate.partnerLogoUrl ?
                        <div className="yt-col _40">
                          <img src={selectedCertificateTemplate.partnerLogoUrl}/>
                        </div>
                        :
                        <span>N/A</span>
                      }
                      <br/>
                      <br/>
                      <p><small><strong>Accreditation Body Logo:</strong></small></p>
                      {selectedCertificateTemplate.accreditationLogoUrl ?
                        <div className="yt-col _40">
                          <img src={selectedCertificateTemplate.accreditationLogoUrl}/>
                        </div>
                        :
                        <span>N/A</span>
                      }
                      <p><small><strong>Accreditation Body Description:</strong></small></p>
                      <p>{selectedCertificateTemplate.accreditationDescription}</p>
                      <br/>
                      <br/>
                      <p><small><strong>Bottom Right Status:</strong></small></p>
                      <p>{_.startCase(selectedCertificateTemplate.bottomRightStatus)}</p>
                      { selectedCertificateTemplate.bottomRightStatus === 'overrideSponsorLogo' ? 
                        <div className="yt-col _40">
                          <img src={selectedCertificateTemplate.bottomRightLogoUrl}/>
                        </div>
                        :
                        null 
                      }
                    </div>
                    
                  </div>
                  <div className="yt-col full s_40 m_30">
                    <div className="content-container">
                      <p><strong>Status</strong></p>
                      <hr/>
                      <p>
                        Created on {DateTime.fromISO(selectedCertificateTemplate.created).toLocaleString(DateTime.DATETIME_FULL)}
                        { selectedCertificateTemplate._createdBy && userMap[selectedCertificateTemplate._createdBy] ?
                          <span> by
                            <Link to={`/admin/users/${selectedCertificateTemplate._createdBy}`} target="_blank"> {userMap[selectedCertificateTemplate._createdBy].username}</Link>
                          </span>
                          : null
                        }
                      </p>
                      <p><small><em>Last updated {DateTime.fromISO(selectedCertificateTemplate.updated).toLocaleString(DateTime.DATETIME_FULL)}</em></small></p>
                      <br/>
                      
                    </div>

                  </div>
                  <div className="yt-col full">
                    <div className="content-container">
                      <p><strong>Certificate Preview</strong></p>
                      <hr/>
                      <CertificateTemplatePreview 
                        certificateTemplate={selectedCertificateTemplate}
                      />
                    </div>
                  </div>
                </div>
                
              </div>
            }
          </div>
        </section>
      </div>
    )
  }
}

AdminSingleCertificateTemplate.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store, ownProps) => {
  /**
   * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
   * differentiated from the React component's internal state
   */


  return {
    defaultQuestion: store.question.defaultItem
    , certificateTemplateStore: store.certificateTemplate
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSingleCertificateTemplate)
);



// WEBPACK FOOTER //
// modules/certificateTemplate/views/AdminSingleCertificateTemplate.js.jsx